import React from 'react';
import { scoreToGrade } from '../utils/GradeUtils';

interface PixelGradeBadgeProps {
  score: number;
  size?: number; // px, default 48
}

// 8x8 pixel letter patterns for each grade letter
const LETTER_PATTERNS: Record<string, number[][]> = {
  'A': [
    [0,0,1,1,1,1,0,0],
    [0,1,1,0,0,1,1,0],
    [1,1,0,0,0,0,1,1],
    [1,1,0,0,0,0,1,1],
    [1,1,1,1,1,1,1,1],
    [1,1,0,0,0,0,1,1],
    [1,1,0,0,0,0,1,1],
    [1,1,0,0,0,0,1,1],
  ],
  'B': [
    [1,1,1,1,1,1,0,0],
    [1,1,0,0,0,1,1,0],
    [1,1,0,0,0,1,1,0],
    [1,1,1,1,1,1,0,0],
    [1,1,0,0,0,1,1,0],
    [1,1,0,0,0,0,1,1],
    [1,1,0,0,0,1,1,0],
    [1,1,1,1,1,1,0,0],
  ],
  'C': [
    [0,0,1,1,1,1,0,0],
    [0,1,1,0,0,1,1,0],
    [1,1,0,0,0,0,0,0],
    [1,1,0,0,0,0,0,0],
    [1,1,0,0,0,0,0,0],
    [1,1,0,0,0,0,0,0],
    [0,1,1,0,0,1,1,0],
    [0,0,1,1,1,1,0,0],
  ],
  'D': [
    [1,1,1,1,1,0,0,0],
    [1,1,0,0,1,1,0,0],
    [1,1,0,0,0,1,1,0],
    [1,1,0,0,0,1,1,0],
    [1,1,0,0,0,1,1,0],
    [1,1,0,0,0,1,1,0],
    [1,1,0,0,1,1,0,0],
    [1,1,1,1,1,0,0,0],
  ],
  'F': [
    [1,1,1,1,1,1,1,1],
    [1,1,0,0,0,0,0,0],
    [1,1,0,0,0,0,0,0],
    [1,1,1,1,1,1,0,0],
    [1,1,0,0,0,0,0,0],
    [1,1,0,0,0,0,0,0],
    [1,1,0,0,0,0,0,0],
    [1,1,0,0,0,0,0,0],
  ],
};

function getPixelOpacity(score: number, row: number, col: number): number {
  // Higher scores = all pixels crisp. Lower scores = pixels degrade from edges
  if (score >= 98) return 1.0;
  if (score >= 95) return 0.95;

  // Calculate distance from center (0-1)
  const cx = 3.5, cy = 3.5;
  const dist = Math.sqrt((col - cx) ** 2 + (row - cy) ** 2) / 5;

  // Degradation factor: lower score = more edge pixels fade
  const degradation = Math.max(0, (100 - score) / 50); // 0 at 100, 1 at 50, 2 at 0
  const fadeThreshold = 1 - degradation * 0.7;

  if (dist > fadeThreshold) {
    const fade = Math.max(0, 1 - (dist - fadeThreshold) * 3);
    return score < 30 ? fade * 0.3 : fade * 0.6;
  }
  return score >= 80 ? 0.95 : score >= 60 ? 0.85 : 0.7;
}

const PixelGradeBadge: React.FC<PixelGradeBadgeProps> = ({ score, size = 48 }) => {
  const { grade, color } = scoreToGrade(score);
  const letter = grade.charAt(0);
  const pattern = LETTER_PATTERNS[letter] || LETTER_PATTERNS['F'];
  const cellSize = size / 8;
  const gap = cellSize * 0.12;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`Trust Score Grade: ${grade} (${score}%)`}
      style={{ filter: score >= 95 ? `drop-shadow(0 0 ${size * 0.15}px ${color}40)` : undefined }}
    >
      <title>Grade {grade} — {score}%</title>
      {pattern.map((row, ry) =>
        row.map((cell, cx) => {
          if (!cell) return null;
          const opacity = getPixelOpacity(score, ry, cx);
          return (
            <rect
              key={`${ry}-${cx}`}
              x={cx * cellSize + gap}
              y={ry * cellSize + gap}
              width={cellSize - gap * 2}
              height={cellSize - gap * 2}
              rx={cellSize * 0.1}
              fill={color}
              opacity={opacity}
            />
          );
        })
      )}
      {/* Modifier (+/-) */}
      {grade.length > 1 && (
        <text
          x={size * 0.85}
          y={size * 0.25}
          fontSize={size * 0.22}
          fill={color}
          textAnchor="middle"
          fontWeight="bold"
          fontFamily="DM Sans, system-ui"
        >
          {grade.charAt(1)}
        </text>
      )}
    </svg>
  );
};

export default PixelGradeBadge;
